// Import financing: loan maths, reference lists and form validation shared by the
// public partner page, the partner portal, the customer financing page and admin.
// Tables: finance_partners, finance_requests, finance_offers (see migration
// 20261001100000_catalog_accounts_finance.sql, section 3).
import { z } from "zod";

// ---------------------------------------------------------------------------
// Loan maths
// ---------------------------------------------------------------------------

function validLoan(principal: number, aprPercent: number, months: number) {
  return (
    Number.isFinite(principal) &&
    Number.isFinite(aprPercent) &&
    Number.isFinite(months) &&
    principal > 0 &&
    aprPercent >= 0 &&
    Number.isInteger(months) &&
    months >= 1
  );
}

/**
 * Fixed monthly instalment for a fully amortising loan (standard annuity formula):
 *   M = P * r / (1 - (1 + r)^-n), with r = APR / 12 / 100.
 * A 0% APR repays the principal in equal instalments. Returns null for invalid input.
 */
export function monthlyPayment(
  principal: number,
  aprPercent: number,
  months: number,
): number | null {
  if (!validLoan(principal, aprPercent, months)) return null;
  const r = aprPercent / 100 / 12;
  if (r === 0) return principal / months;
  return (principal * r) / (1 - Math.pow(1 + r, -months));
}

/** Everything the borrower pays back: all instalments plus upfront fees. */
export function totalRepayable(
  principal: number,
  aprPercent: number,
  months: number,
  feesGhs = 0,
): number | null {
  const m = monthlyPayment(principal, aprPercent, months);
  if (m === null || !Number.isFinite(feesGhs) || feesGhs < 0) return null;
  return m * months + feesGhs;
}

/** Interest plus fees: what the credit costs on top of the amount borrowed. */
export function totalCostOfCredit(
  principal: number,
  aprPercent: number,
  months: number,
  feesGhs = 0,
): number | null {
  const total = totalRepayable(principal, aprPercent, months, feesGhs);
  return total === null ? null : total - principal;
}

/** Round to pesewas for storage/display. */
export function roundGhs(value: number): number {
  return Math.round(value * 100) / 100;
}

export function formatGhsAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return "-";
  return `GHS ${value.toLocaleString("en-GH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// ---------------------------------------------------------------------------
// Reference lists
// ---------------------------------------------------------------------------

export type Option<T extends string = string> = { value: T; label: string };

export const GHANA_REGIONS = [
  "Ahafo",
  "Ashanti",
  "Bono",
  "Bono East",
  "Central",
  "Eastern",
  "Greater Accra",
  "North East",
  "Northern",
  "Oti",
  "Savannah",
  "Upper East",
  "Upper West",
  "Volta",
  "Western",
  "Western North",
] as const;
export const NATIONWIDE = "Nationwide";

export const INSTITUTION_TYPES = [
  { value: "bank", label: "Bank" },
  { value: "savings_and_loans", label: "Savings & loans company" },
  { value: "microfinance", label: "Microfinance institution" },
  { value: "leasing", label: "Leasing / asset finance company" },
  { value: "fintech", label: "Fintech lender" },
  { value: "other", label: "Other licensed lender" },
] as const satisfies readonly Option[];
export type InstitutionType = (typeof INSTITUTION_TYPES)[number]["value"];

export const FINANCE_PRODUCTS = [
  { value: "vehicle_loan", label: "Vehicle loans" },
  { value: "asset_finance", label: "Asset / equipment finance" },
  { value: "trade_finance", label: "Trade / stock finance" },
  { value: "invoice_finance", label: "Invoice finance" },
  { value: "letters_of_credit", label: "Letters of credit" },
] as const satisfies readonly Option[];
export type FinanceProduct = (typeof FINANCE_PRODUCTS)[number]["value"];

export const FINANCE_PURPOSES = [
  { value: "vehicle", label: "Vehicle" },
  { value: "stock_inventory", label: "Stock / inventory for resale" },
  { value: "equipment", label: "Equipment or machinery" },
  { value: "other", label: "Other import" },
] as const satisfies readonly Option[];
export type FinancePurpose = (typeof FINANCE_PURPOSES)[number]["value"];

export const APPLICANT_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "business", label: "Business" },
] as const satisfies readonly Option[];
export type ApplicantType = (typeof APPLICANT_TYPES)[number]["value"];

/** Bands only: we never ask for or store an exact salary or revenue figure. */
export const INCOME_BANDS = [
  { value: "under_3k", label: "Under GHS 3,000 / month" },
  { value: "3k_6k", label: "GHS 3,000 – 6,000 / month" },
  { value: "6k_12k", label: "GHS 6,000 – 12,000 / month" },
  { value: "12k_25k", label: "GHS 12,000 – 25,000 / month" },
  { value: "25k_50k", label: "GHS 25,000 – 50,000 / month" },
  { value: "50k_150k", label: "GHS 50,000 – 150,000 / month" },
  { value: "over_150k", label: "Over GHS 150,000 / month" },
  { value: "prefer_not", label: "Prefer not to say" },
] as const satisfies readonly Option[];
export type IncomeBand = (typeof INCOME_BANDS)[number]["value"];

export const EMPLOYMENT_TYPES = [
  { value: "salaried", label: "Salaried employee" },
  { value: "self_employed", label: "Self-employed" },
  { value: "business_owner", label: "Business owner / company" },
  { value: "other", label: "Other" },
] as const satisfies readonly Option[];
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number]["value"];

export const TENURE_OPTIONS = [3, 6, 12, 18, 24, 36, 48, 60, 72, 84] as const;

export const MAX_REQUEST_GHS = 50_000_000;

/** Service expectation stated to partners (policy value, adjust here). */
export const PARTNER_RESPONSE_BUSINESS_DAYS = 2;

export function labelFor(
  options: readonly Option[],
  value: string | null | undefined,
): string {
  if (!value) return "-";
  return options.find((o) => o.value === value)?.label ?? value;
}

const values = <T extends string>(opts: readonly Option<T>[]) =>
  opts.map((o) => o.value) as [T, ...T[]];

// ---------------------------------------------------------------------------
// Validation (form values arrive as strings; schemas coerce and normalise)
// ---------------------------------------------------------------------------

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Keep this under ${max} characters`)
    .optional()
    .transform((v) => (v ? v : null));

const emptyToUndefined = (v: unknown) =>
  v === "" || v === null || v === undefined ? undefined : v;

const optionalAmount = z.preprocess(
  emptyToUndefined,
  z.coerce
    .number({ invalid_type_error: "Enter a number" })
    .min(0, "Must be zero or more")
    .max(MAX_REQUEST_GHS * 10, "Amount is too large")
    .optional(),
);

const isoDate = /^\d{4}-\d{2}-\d{2}$/;

function todayIso(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export const partnerApplicationSchema = z
  .object({
    institution_name: z
      .string()
      .trim()
      .min(2, "Institution name is required")
      .max(200),
    institution_type: z.enum(values(INSTITUTION_TYPES), {
      errorMap: () => ({ message: "Choose the type of institution" }),
    }),
    license_number: optionalText(80),
    contact_name: z.string().trim().min(2, "Contact name is required").max(120),
    contact_email: z.string().trim().email("Enter a valid work email").max(255),
    contact_phone: optionalText(40),
    website: z
      .string()
      .trim()
      .max(255)
      .optional()
      .transform((v) => (v ? v : null))
      .refine(
        (v) =>
          v === null || /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i.test(v),
        "Enter a valid website address",
      ),
    products: z
      .array(z.enum(values(FINANCE_PRODUCTS)))
      .min(1, "Select at least one product you offer"),
    min_amount_ghs: optionalAmount,
    max_amount_ghs: optionalAmount,
    indicative_rate: optionalText(120),
    coverage_regions: z
      .array(z.string())
      .min(1, "Select at least one region, or Nationwide")
      .refine(
        (rs) =>
          rs.every(
            (r) =>
              r === NATIONWIDE ||
              (GHANA_REGIONS as readonly string[]).includes(r),
          ),
        "Unknown region",
      ),
    message: optionalText(2000),
  })
  .refine(
    (v) =>
      v.min_amount_ghs === undefined ||
      v.max_amount_ghs === undefined ||
      v.max_amount_ghs >= v.min_amount_ghs,
    {
      message: "Maximum must be at least the minimum",
      path: ["max_amount_ghs"],
    },
  );
export type PartnerApplicationInput = z.input<typeof partnerApplicationSchema>;
export type PartnerApplication = z.output<typeof partnerApplicationSchema>;

export const financeRequestSchema = z
  .object({
    quote_request_id: z.preprocess(
      emptyToUndefined,
      z.string().uuid().optional(),
    ),
    purpose: z.enum(values(FINANCE_PURPOSES), {
      errorMap: () => ({ message: "Choose what the financing is for" }),
    }),
    item_summary: z
      .string()
      .trim()
      .min(3, "Describe the item in a few words")
      .max(300, "Keep this under 300 characters"),
    amount_ghs: z.coerce
      .number({ invalid_type_error: "Enter the amount in GHS" })
      .positive("Enter the amount you need in GHS")
      .max(MAX_REQUEST_GHS, "Maximum request is GHS 50,000,000"),
    down_payment_ghs: optionalAmount,
    tenure_months: z.coerce
      .number({ invalid_type_error: "Choose a repayment period" })
      .int()
      .min(3, "Minimum 3 months")
      .max(84, "Maximum 84 months"),
    applicant_type: z.enum(values(APPLICANT_TYPES), {
      errorMap: () => ({ message: "Choose individual or business" }),
    }),
    income_band: z.enum(values(INCOME_BANDS), {
      errorMap: () => ({ message: "Choose an income band" }),
    }),
    employment_type: z.preprocess(
      emptyToUndefined,
      z.enum(values(EMPLOYMENT_TYPES)).optional(),
    ),
    city: optionalText(80),
    consent: z.literal(true, {
      errorMap: () => ({
        message: "Please confirm consent so partners can see your request",
      }),
    }),
  })
  .refine(
    (v) =>
      v.down_payment_ghs === undefined || v.down_payment_ghs < v.amount_ghs,
    {
      message: "Down payment should be less than the amount requested",
      path: ["down_payment_ghs"],
    },
  );
export type FinanceRequestInput = z.input<typeof financeRequestSchema>;
export type FinanceRequestValues = z.output<typeof financeRequestSchema>;

export function makeOfferSchema(now = new Date()) {
  const today = todayIso(now);
  return z.object({
    amount_ghs: z.coerce
      .number({ invalid_type_error: "Enter the amount in GHS" })
      .positive("Enter the loan amount"),
    apr_percent: z.coerce
      .number({ invalid_type_error: "Enter the APR" })
      .min(0, "APR cannot be negative")
      .max(200, "APR must be 200% or less"),
    tenure_months: z.coerce
      .number({ invalid_type_error: "Enter the tenure" })
      .int("Whole months only")
      .min(1, "At least 1 month")
      .max(120, "At most 120 months"),
    fees_ghs: z.preprocess(
      (v) => (emptyToUndefined(v) === undefined ? 0 : v),
      z.coerce
        .number({ invalid_type_error: "Enter fees in GHS" })
        .min(0, "Fees cannot be negative"),
    ),
    conditions: optionalText(1000),
    valid_until: z.preprocess(
      emptyToUndefined,
      z
        .string()
        .regex(isoDate, "Use a valid date")
        .refine((d) => d >= today, "Validity date cannot be in the past")
        .optional(),
    ),
  });
}
export const offerSchema = makeOfferSchema();
export type OfferInput = z.input<ReturnType<typeof makeOfferSchema>>;
export type OfferValues = z.output<ReturnType<typeof makeOfferSchema>>;

/** First error per field, for inline messages. */
export function fieldErrors(error: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = String(issue.path[0] ?? "form");
    if (!(key in out)) out[key] = issue.message;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Row types (tables are not in the generated Supabase types yet)
// ---------------------------------------------------------------------------

export type PartnerStatus = "pending" | "approved" | "rejected" | "suspended";
export type RequestStatus =
  "open" | "offered" | "accepted" | "withdrawn" | "closed";
export type OfferStatus =
  "offered" | "accepted" | "declined" | "expired" | "withdrawn";

export interface FinancePartnerRow {
  id: string;
  user_id: string | null;
  institution_name: string;
  institution_type: string;
  license_number: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  website: string | null;
  products: string[];
  min_amount_ghs: number | null;
  max_amount_ghs: number | null;
  indicative_rate: string | null;
  coverage_regions: string[];
  message: string | null;
  status: PartnerStatus;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FinanceRequestRow {
  id: string;
  user_id: string;
  quote_request_id: string | null;
  purpose: string;
  item_summary: string;
  amount_ghs: number;
  down_payment_ghs: number | null;
  tenure_months: number;
  applicant_type: string;
  income_band: string | null;
  employment_type: string | null;
  city: string | null;
  consent_to_share: boolean;
  consented_at: string;
  status: RequestStatus;
  created_at: string;
  updated_at: string;
}

export interface FinanceOfferRow {
  id: string;
  request_id: string;
  partner_id: string;
  amount_ghs: number;
  apr_percent: number;
  tenure_months: number;
  monthly_payment_ghs: number | null;
  fees_ghs: number;
  conditions: string | null;
  valid_until: string | null;
  status: OfferStatus;
  created_at: string;
}

export interface FinancePartnerPublicRow {
  id: string;
  institution_name: string;
  institution_type: string;
  products: string[];
  website: string | null;
}

/** Postgres numeric comes back as a string from PostgREST in some setups. */
export function num(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function isOfferExpired(
  offer: Pick<FinanceOfferRow, "valid_until">,
  now = new Date(),
): boolean {
  return offer.valid_until !== null && offer.valid_until < todayIso(now);
}
